import { useEffect, useState, useCallback, useRef } from 'react';
import { useSocketContext } from '../contexts/SocketContext';
import { CustomButton } from '../types';

/**
 * Custom hook for handling Socket.IO events related to buttons
 * Manages real-time updates for button state changes
 * 
 * @param onUpdate Optional callback function to execute when buttons are updated
 * @param counterId The ID of the current counter to filter buttons
 * @returns Object with button socket operations and state
 */
export const useButtonSocket = (
  onUpdate?: (buttons: CustomButton[]) => void,
  counterId?: string
) => {
  const { socket, isConnected } = useSocketContext();
  const [error, setError] = useState<string | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle socket events for button updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleButtonUpdate = (updatedButtons: CustomButton[]) => {
      // If we get a large number of buttons, this is likely ALL buttons from the system
      // Let's ignore this update and fetch only the buttons for the current counter
      if (counterId && updatedButtons.length > 10) {
        // Instead of attempting to filter, we should signal that a focused refetch is needed
        if (onUpdate) {
          // Return an empty array to signal that a fresh fetch is needed
          onUpdate([]);
        }
        return;
      }
      
      // Clear any pending update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Debounce the update to avoid rapid re-renders
      updateTimeoutRef.current = setTimeout(() => {
        if (onUpdate) {
          onUpdate(updatedButtons);
        }
      }, 500); // 500ms debounce period
    };
    
    // Register event listener
    socket.on('buttonUpdate', handleButtonUpdate);
    
    // Clean up event listeners on unmount
    return () => {
      socket.off('buttonUpdate', handleButtonUpdate);
      
      // Clear any pending timeouts
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [socket, isConnected, onUpdate, counterId]);

  /**
   * Emit a button update event to notify the server of changes
   * 
   * @param buttonId ID of the button that was updated
   */
  const emitButtonUpdate = useCallback((buttonId: string) => {
    if (!socket || !isConnected) {
      setError('Socket not connected. Unable to emit button update.');
      return false;
    }

    try {
      socket.emit('buttonUpdate', { buttonId, counterId });
      return true;
    } catch (err) {
      console.error('Error emitting button update:', err);
      setError('Failed to emit button update');
      return false;
    }
  }, [socket, isConnected, counterId]);

  // Reset error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    emitButtonUpdate,
    error,
    clearError,
    isConnected
  };
}; 